import Effects from 'Effects';
import Diode from './Diode.js';
import {bulkUpdate} from 'utils/bulkUpdate.js';
import Tone from 'tone';
import {AudioParamPercentageValueParameter,
        AudioParamValueParameter} from 'parameters/AbstractAudioParamParameter.js';
import {AbstractParameter} from 'parameters/AbstractParameter.js';
        
import PolyParameter from 'parameters/PolyParameter.js';

class FrequencyShifter extends Effects.Effect {

    constructor(sequencerEngine) {
        super('Frequency Shifter', () => new Tone.Gain(0.5), sequencerEngine); 

        this._numBanks = 4;

        this.outputNode = new Tone.Merge(1);
        this.wetIn = new Tone.Gain(0.5);
        this.delaySpread = new Tone.Signal(0);
        this.effectNode.connect(this.wetIn);
        this.vInGains = [];
        this.delays = [[], []];
        this.createGraph();

        this.delaySpreadParameter = new AudioParamValueParameter(
            'delaySpread', this.delaySpread,
            {
                defaultValue: 0,
                min: 0,
                max: 1,
                automationRate: 'A_RATE'
            }, 0.07);
        this.registerParameter(this.delaySpreadParameter);
        
 
        this.frequencyParameter = new PolyParameter(
            'frequency', [this.sineA.frequency, this.sineB.frequency],
            {
                defaultValue: 30,
                min: 1,
                max: 10000
            });
        
        this.registerParameter(this.frequencyParameter);
        this.delayDirectionParameter = new AbstractParameter(
            "delayDirection", this, (o, x) => o.setDelayDirection(x), {
                defaultValue: true,
            }
        );
        this.updateInitialValues();
    }

    createGraph() {
        this.sineA = new Tone.Oscillator(0, "sine");
        this.sineA.phase = 0;
        this.sineB = new Tone.Oscillator(0, "sine");
        this.sineB.phase = 90;

        this.sineA.start(0);
        this.sineB.start(0);

        this.createHilbertTransformer();
    }

    ring(osc, wetIn, negate) {
        let output = new Tone.Compressor(-12, 3);
        let vInGain = new Tone.Gain(0.5);
        this.vInGains.push(vInGain);
        let vInInverter1 = new Tone.Gain(-1);
        let vInInverter2 = new Tone.Gain(-1);
        let vInInverter3 = new Tone.Gain(-1);
        let vcInverter1 = new Tone.Gain(-1);
        let vInDiode1 = new Diode(this.sequencerEngine).effectNode;
        let vInDiode2 = new Diode(this.sequencerEngine).effectNode;
        let vcDiode3 = new Diode(this.sequencerEngine).effectNode;
        let vcDiode4 = new Diode(this.sequencerEngine).effectNode;

        osc.connect(vInGain); // checked
        vInGain.connect(vInInverter1); //
        vInInverter1.connect(vInInverter2); // 
        vInInverter1.connect(vInDiode2); // 

        vInInverter2.connect(vInDiode1); // 
        vInDiode1.connect(vInInverter3); // 
        vInDiode2.connect(vInInverter3); // 
        vInInverter3.connect(output);// 
        vInGain.connect(vcDiode4); //

        vInGain.connect(vcInverter1); // 
        vcInverter1.connect(vcDiode3); //

        vcDiode3.connect(output); // 
        vcDiode4.connect(output); // 

        wetIn.connect(vcInverter1); //
        wetIn.connect(vcDiode4); //

        return {
            output,
            dispose: () => {
                vInGain.dispose();
                output.dispose();
                vInDiode1.dispose();
                vInDiode2.dispose();
                vcDiode3.dispose();
                vcDiode4.dispose();
                vInInverter1.dispose();
                vInInverter2.dispose();
                vInInverter3.dispose();
                vcInverter1.dispose();
            }
        };
    }

    createHilbertTransformer() {

        let splitter = new Tone.Split();
        this.wetIn.connect(splitter);
        let ring1L = this.ring(this.sineA, splitter.left);
        let ring2L = this.ring(this.sineB, splitter.left);
        let ring1R = this.ring(this.sineA, splitter.right, true);
        let ring2R = this.ring(this.sineB, splitter.right, true);

        let disposes = [
            ring1L.dispose,
            ring2L.dispose,
            ring1R.dispose,
            ring2R.dispose,
            () => splitter.dispose()
        ];

        ring1L = ring1L.output;
        ring2L = ring2L.output;
        ring1R = ring1R.output;
        ring2R = ring2R.output;


        for (let j=0; j < 2; j++) {
            let freq = 50;
            for (let i=0; i < this.numBanks; i++) {
                let q =  (this.numBanks / 8)*((i + 1) / 4);
                let bluntForce = this.delayDirection ? this.numBanks - i : i;
                let delayTime =  bluntForce / 1000.0;
                let bandpass = new Tone.Filter(freq, 'bandpass', -24);
                bandpass.Q.value = q;
                let allpass = new Tone.Filter(freq, 'allpass');
                allpass.Q.value = q;
                
                freq += 10000.0 / this.numBanks;
                
                let delay = new Tone.Delay(delayTime);
                delay.delayTime.input.automationRate = 'A_RATE';
                let delayBlunter = new Tone.Gain(bluntForce / this.numBanks);
                this.delaySpread.chain(delayBlunter, delay.delayTime);
                delay.delayTime.value = delayTime;
                
                if (j === 0) {
                    bandpass.chain(allpass, delay, this.outputNode.left);
                    ring1L.connect(bandpass);
                    ring2L.connect(bandpass);
                }  else {
                    bandpass.chain(allpass, delay, this.outputNode.right);
                    ring1R.connect(bandpass);
                    ring2R.connect(bandpass);
                }
                
                this.delays[j].push({
                    delay,
                    delayBlunter
                });
                
                disposes.push(
                    () => {
                        bandpass.dispose();
                        allpass.dispose();
                        delay.dispose();
                        delayBlunter.dispose();
                    });
            }
        }

        this.disposeList = disposes;
    }

    set numBanks(numBanks) {
        console.log('setting numbank=', numBanks);
        this._numBanks = numBanks;
        if (this.disposeList) {
            this.disposeList.forEach(d => d());
            this.disposeList = undefined;
        }
        this.vInGains = [];
        this.delays = [[], []];
        this.wetIn.disconnect();
        this.createHilbertTransformer();
    }

    dispose() {
        super.dispose();

        this.disposeList.forEach(
            d => d());

        this.sineA.dispose();
        this.sineB.dispose();
    }

    randomizeDelays() {
        for (let i in this.delays) {
            for (let {delay} of this.delays[i]) {
                delay.delayTime.value = Math.random() / 2.0;
            }
        }
    }

    clearRandom() {
        this.delayDirectionParameter.refresh();
    }

    get delayDirection() {
        if (!this.delayDirectionParameter) {
            return true;
        }
        return this.delayDirectionParameter.get();
    }

    setDelayDirection(hiToLow=true) {
        for (let j in this.delays) {
            for (let i in this.delays[j]) {
                let bluntForce = hiToLow ? this.numBanks - i : i;
                let {delay, delayBlunter} = this.delays[j][i];
                delay.delayTime.value = bluntForce / 1000.0;
                delayBlunter.gain.value = bluntForce / this.numBanks;
            }
        }
    }

    get numBanks() {
        return this._numBanks;
    }

    connect(node) {
        this.connectedNode = node;
        this.outputNode.connect(node);
    }

    disconnect(node) {
        this.outputNode.disconnect(node);
    }

    getEffectCopy(sequencerEngine=this.sequencerEngine, ignoreId=true) {
        var copy = new FrequencyShifter(sequencerEngine);
        copy.fromJson(this.getJson(), ignoreId);
        return copy;
    }

    getJson() {
        let json = {
            ... super.getJson(),
            frequency: this.frequencyParameter.get(true),
            delaySpread: this.delaySpreadParameter.get(true),
            delayDirection: this.delayDirectionParameter.get(true),
            numBanks: this.numBanks
        };

        json = this.saveParameterToJson(json, 'delaySpreadParameter', this.delaySpreadParameter);
        json = this.saveParameterToJson(json, 'delayDirectionParameter', this.delayDirectionParameter);
        json = this.saveParameterToJson(json, 'frequencyParameter', this.frequencyParameter);
        return json;
    }

    fromJson(json, isCopy) {
        let bulk = super.fromJson(json, isCopy);
        bulkUpdate(this.frequencyParameter, json.frequency, bulk);
        bulkUpdate(this.delaySpreadParameter, json.delaySpread, bulk);
        bulkUpdate(this.delayDirectionParameter, json.delayDirection, bulk);

        if (json.numBanks && json.numBanks !== this.numBanks) {
            this.numBanks = json.numBanks;
        }

        bulk = {
            ... bulk,
            ... this.loadParameterFromJson(json, 'frequencyParameter', this.frequencyParameter, isCopy),
            ... this.loadParameterFromJson(json, 'delaySpreadParameter', this.delaySpreadParameter, isCopy),
            ... this.loadParameterFromJson(json, 'delayDirectionParameter', this.delayDirectionParameter, isCopy)
        };
        return bulk;
    }
}

export default FrequencyShifter;
